"use client";

import { useEffect, useState } from "react";

import { getDevicePhotoBlob } from "@/lib/api/endpoints/devices";
import { cn } from "@/lib/utils/cn";

type PrivateDeviceImageProps = {
  photoPath: string;
  alt: string;
  className?: string;
  /** Use a pulse placeholder instead of loading/error copy (e.g. table thumbnails). */
  silent?: boolean;
};

export function PrivateDeviceImage({ photoPath, alt, className, silent = false }: PrivateDeviceImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    setSrc(null);
    setFailed(false);

    void getDevicePhotoBlob(photoPath)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photoPath]);

  if (failed) {
    if (silent) {
      return <div className={className} aria-hidden />;
    }
    return <div className={className}>Photo unavailable.</div>;
  }

  if (!src) {
    if (silent) {
      return (
        <div
          className={cn(className, "animate-pulse bg-muted-bg/50")}
          aria-busy="true"
          aria-label="Loading photo"
        />
      );
    }
    return <div className={className}>Loading photo...</div>;
  }

  return <img src={src} alt={alt} className={className} loading="lazy" />;
}
